import { useCallback, useState } from 'react';
import { dateToString } from '~/helpers/dateToString';
import useTranslation from '~/hooks/useTranslation';
import cnh from '~/helpers/cnh';
import buttonStyles from '~/styles/button.module.css';
import useCalendarContactMutation, { CalendarContactFormData } from '~/hooks/calendar/useCalendarContactMutation';
import contactFormStyles from '~/styles/calendar/contact-form.module.css';
import Alert from '~/components/General/Alert';
import { useOptions } from '~/hooks/useOptions';
import { InferredCalendarOptions } from '~/types/options';
import FormGroup from '~/components/Form/FormGroup';
import PlainTextField from '~/components/Form/PlainTextField';
import PlainCheckbox from '~/components/Form/PlainCheckbox';

type Props = {
  selectedDay?: Date;
  pax: number;
  packageId: number;
};

export default function ContactForm({
  selectedDay,
  pax,
  packageId,
}: Props) {
  const translate = useTranslation();
  const contactMutation = useCalendarContactMutation();
  const options = useOptions() as InferredCalendarOptions;

  const [formData, setFormData] = useState<CalendarContactFormData>({
    name: '',
    email: '',
    message: '',
    agreement: false,

    // Metadata
    pax,
    package_id: packageId,
    locale: options.locale,
    office_id: options.officeId,
    department_id: options.departmentId,
    date: selectedDay ? dateToString(selectedDay) : undefined,
  });

  const onSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    contactMutation.mutate({ formState: formData });
  }, [contactMutation, formData]);

  if (contactMutation.isSuccess) {
    return (
      <div className={contactFormStyles.contactFormSuccess}>
        {translate('calendar.contact_modal_success')}
      </div>
    );
  }

  const contactError = contactMutation.error;

  return (
    <form method="post" onSubmit={onSubmit}>
      <Alert
        type="danger"
        error={contactError}
      />

      <FormGroup>
        <PlainTextField
          label={translate('form.name')}
          value={formData.name}
          onChange={(event) => setFormData({ ...formData, name: event.target.value })}
          required
        />
      </FormGroup>

      <FormGroup>
        <PlainTextField
          label={translate('form.email')}
          fieldType="email"
          value={formData.email}
          onChange={(event) => setFormData({ ...formData, email: event.target.value })}
          required
        />
      </FormGroup>

      <FormGroup>
        <PlainTextField
          label={translate('form.message')}
          value={formData.message}
          onChange={(event) => setFormData({ ...formData, message: event.target.value })}
          rows={3}
          required
        />
      </FormGroup>

      <FormGroup>
        <PlainCheckbox
          label={translate('form.gdpr', {
            agreementUrl: options.termsOfServiceUrl,
            privacyUrl: options.privacyPolicyUrl,
          })}
          checked={formData.agreement}
          onChange={(checked) => setFormData({ ...formData, agreement: checked })}
          required
        />
      </FormGroup>

      <button
        type="submit"
        className={cnh(buttonStyles.btn, buttonStyles.btnPrimary, buttonStyles.btnFull)}
        disabled={contactMutation.isPending}
      >
        {translate('calendar.contact_modal_submit')}
      </button>
    </form>
  );
}
